import { Container, Text } from 'pixi.js';
import type { Disposable } from '../../core/Event';
import type { HigherLowerModel } from '../../model/higherlower/HigherLowerModel';
import { BalanceDisplay } from '../../widgets/BalanceDisplay';
import { PrimaryButton } from '../../widgets/PrimaryButton';
import type { SceneComponent, SceneLayoutContext } from '../SceneComponent';

const FONT_FAMILY = 'system-ui, -apple-system, sans-serif';

export interface HigherLowerHudCallbacks {
  onPlay: () => void;
  onHigher: () => void;
  onLower: () => void;
  onCashOut: () => void;
}

/**
 * Higher/Lower HUD: balance + streak chip + four action buttons (PLAY
 * before round; HIGHER, LOWER, CASH OUT during round). Subscribes to
 * model events so the multiplier/streak labels update without scene
 * wiring.
 */
export class HigherLowerHudComponent extends Container implements SceneComponent {
  private readonly balance: BalanceDisplay;
  private readonly streakLabel: Text;
  private readonly playButton: PrimaryButton;
  private readonly higherButton: PrimaryButton;
  private readonly lowerButton: PrimaryButton;
  private readonly cashOutButton: PrimaryButton;
  private readonly statusLabel: Text;
  private readonly subscriptions: Disposable[] = [];

  constructor(
    private readonly model: HigherLowerModel,
    private readonly callbacks: HigherLowerHudCallbacks,
  ) {
    super();
    this.balance = new BalanceDisplay(this.model.balance);
    this.streakLabel = this.buildText('', 14, 0xffffff);
    this.streakLabel.alpha = 0.85;
    this.statusLabel = this.buildText(
      `tap PLAY to deal a card · ${this.model.bet}`,
      13,
      0xffffff,
    );
    this.statusLabel.alpha = 0.7;

    this.playButton = new PrimaryButton({
      label: this.idleLabel(),
      width: 200,
      fillActive: 0xff6bd6,
      fillHover: 0x6b3d77,
      textColor: 0xffffff,
      onTap: () => this.callbacks.onPlay(),
    });

    this.higherButton = new PrimaryButton({
      label: '⬆ HIGHER',
      width: 130,
      fillActive: 0x42e2f5,
      fillHover: 0x2a4d75,
      textColor: 0x1a0830,
      textSize: 16,
      letterSpacing: 1,
      onTap: () => this.callbacks.onHigher(),
    });
    this.higherButton.visible = false;

    this.lowerButton = new PrimaryButton({
      label: '⬇ LOWER',
      width: 130,
      fillActive: 0xff8a3d,
      fillHover: 0x6b4520,
      textColor: 0x1a0830,
      textSize: 16,
      letterSpacing: 1,
      onTap: () => this.callbacks.onLower(),
    });
    this.lowerButton.visible = false;

    this.cashOutButton = new PrimaryButton({
      label: 'CASH OUT',
      width: 140,
      fillActive: 0xfff59d,
      fillHover: 0x6b6020,
      textColor: 0x1a0830,
      textSize: 14,
      letterSpacing: 1,
      onTap: () => this.callbacks.onCashOut(),
    });
    this.cashOutButton.visible = false;

    this.addChild(
      this.balance,
      this.streakLabel,
      this.playButton,
      this.higherButton,
      this.lowerButton,
      this.cashOutButton,
      this.statusLabel,
    );

    this.subscriptions.push(
      this.model.events.on('balance-changed', () => {
        this.balance.setAmount(this.model.balance);
      }),
      this.model.events.on('round-started', () => {
        this.playButton.visible = false;
        this.higherButton.visible = true;
        this.lowerButton.visible = true;
        this.cashOutButton.visible = false; // need at least one correct guess to cash out
        this.statusLabel.text = `card is ${this.model.currentCard} — higher or lower?`;
        this.refreshStreakLabel();
      }),
      this.model.events.on('prediction-resolved', (event) => {
        this.refreshStreakLabel();
        if (!event.correct) {
          this.statusLabel.text = `${event.fromCard} → ${event.toCard} · WRONG · -${this.model.bet}`;
          this.higherButton.visible = false;
          this.lowerButton.visible = false;
          this.cashOutButton.visible = false;
          this.scheduleResetToPlay();
        } else {
          this.statusLabel.text = `${event.fromCard} → ${event.toCard} · ${event.multiplierAfter}× banked · keep going?`;
          this.cashOutButton.visible = true;
          this.cashOutButton.setLabel(this.cashOutLabel());
        }
      }),
      this.model.events.on('cashed-out', (event) => {
        this.statusLabel.text = `cashed at ${event.multiplier}× · +${event.winnings}`;
        this.higherButton.visible = false;
        this.lowerButton.visible = false;
        this.cashOutButton.visible = false;
        this.scheduleResetToPlay();
      }),
      this.model.events.on('not-enough-coins', (event) => {
        this.statusLabel.text = `not enough coins for ${event.required}`;
      }),
    );
  }

  layout(ctx: SceneLayoutContext): void {
    const { centerX, machineTop, machineBottom, spinButtonY } = ctx;
    this.balance.x = centerX;
    this.balance.y = Math.max(16, machineTop - 96);
    this.streakLabel.x = centerX;
    this.streakLabel.y = Math.max(this.balance.y + 56, machineTop - 18);

    // Bottom action row — PLAY, or [HIGHER · LOWER · CASH OUT] depending on state.
    this.playButton.x = centerX;
    this.playButton.y = spinButtonY;
    this.higherButton.x = centerX - 150;
    this.higherButton.y = spinButtonY;
    this.lowerButton.x = centerX;
    this.lowerButton.y = spinButtonY;
    this.cashOutButton.x = centerX + 150;
    this.cashOutButton.y = spinButtonY;

    this.statusLabel.x = centerX;
    this.statusLabel.y = machineBottom + 18;
  }

  destroy(): void {
    for (const sub of this.subscriptions) sub.dispose();
    this.subscriptions.length = 0;
    this.balance.destroyDisplay();
    super.destroy({ children: true });
  }

  // -------- private --------

  private cashOutLabel(): string {
    const winnings = Math.floor(this.model.bet * this.model.multiplier);
    return `CASH ${winnings}`;
  }

  private idleLabel(): string {
    return `PLAY · ${this.model.bet}`;
  }

  private refreshStreakLabel(): void {
    if (this.model.streak === 0) {
      this.streakLabel.text = `streak 0 · ${this.model.multiplier}×`;
    } else {
      this.streakLabel.text = `🔥 streak ${this.model.streak} · ${this.model.multiplier}×`;
    }
  }

  private scheduleResetToPlay(): void {
    setTimeout(() => {
      if (this.model.status === 'lost' || this.model.status === 'cashed-out') {
        this.playButton.visible = true;
        this.playButton.setLabel(this.idleLabel());
        this.statusLabel.text = `tap PLAY for another round · ${this.model.bet}`;
        this.streakLabel.text = '';
      }
    }, 1600);
  }

  private buildText(text: string, size: number, fill: number): Text {
    const t = new Text({
      text,
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: size,
        fontWeight: '700',
        fill,
        align: 'center',
      },
    });
    t.anchor.set(0.5);
    return t;
  }
}
